// What a CLIENT is told when something fails — DISCLOSURE_POLICY 240926
// V1.1 (approved 2026-09-24), category 5 and Schedule B:
//
//   "A client is shown a plain-language message and what to do next, never
//    the system's text."
//
// Database, PostgREST and Edge Function messages name tables, constraints,
// roles and run ids ("new row violates row-level security policy for table
// run_criterion_responses", "run … is assigned to …"). They are class N on
// client surfaces. This module is the only way a respondent screen turns a
// thrown error into words, so the rule has one place to live and one place
// to be tested.
//
// THE REFERENCE. A short code the respondent can quote: the action and the
// UTC moment, e.g. SAVE-20260924-143205Z. It carries nothing about the
// failure itself; it lets LINNFOSS find the matching entry in the Supabase
// API and Edge Function logs by action and time. The system text is also in
// the respondent's own browser (the network panel), which is theirs.
//
// Deliberately NOT here: logging. The console source has no `console.`
// call anywhere, and a client-side log of system text would be one more
// place for it to surface.
//
// PURE: the clock is a parameter, so the reference is testable.

export type ClientAction = "signin" | "access" | "load" | "save" | "submit" | "report";

const CODE: Record<ClientAction, string> = {
  signin: "SIGNIN",
  access: "ACCESS",
  load: "LOAD",
  save: "SAVE",
  submit: "SUBMIT",
  report: "REPORT",
};

const SENTENCE: Record<ClientAction, string> = {
  signin: "You could not be signed in. Please try again.",
  access: "Your access could not be checked. Please try again, or sign out and sign in again.",
  load: "Your assessment could not be loaded. Please reload the page.",
  save: "Your answer was not saved. Please try again.",
  submit: "Your assessment was not submitted, and your answers are unchanged. Please try again.",
  report: "Your report could not be produced. Please reload the page.",
};

export interface ClientMessage {
  message: string;
  reference: string;
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, "0");
}

export function referenceFor(action: ClientAction, at: Date): string {
  const d = `${at.getUTCFullYear()}${pad(at.getUTCMonth() + 1)}${pad(at.getUTCDate())}`;
  const t = `${pad(at.getUTCHours())}${pad(at.getUTCMinutes())}${pad(at.getUTCSeconds())}`;
  return `${CODE[action]}-${d}-${t}Z`;
}

/**
 * The client-safe message for a failure. `_error` is accepted and ignored
 * on purpose: the caller hands over what it caught, and nothing of it is
 * rendered. Taking it keeps every call site uniform (`clientError("save",
 * e)`) and makes "the error was dropped" a property of this module rather
 * than of each screen's discipline.
 */
export function clientError(action: ClientAction, _error: unknown, at: Date = new Date()): ClientMessage {
  const reference = referenceFor(action, at);
  return {
    reference,
    message: `${SENTENCE[action]} If this keeps happening, contact LINNFOSS and quote reference ${reference}.`,
  };
}
